/**
 * TrendChart — shared Recharts time-series chart (SF-3733).
 *
 * A reusable area chart for Eva usage series, consumed by the platform overview
 * and (later) company / user detail pages. Colors are pulled from the antd
 * theme token via `theme.useToken()` rather than hardcoded, so the chart tracks
 * the ServeFirst palette (primary #6E247F). Renders a loading skeleton, an
 * empty state, and an accessible value tooltip.
 */
import { useId } from 'react';
import { Card, Empty, Skeleton, Typography, theme } from 'antd';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatBucketLabel, type Granularity } from '../../utils/date';

const { Text } = Typography;

/** A single point the chart can plot. */
export interface TrendPoint {
  bucket: string;
  value: number;
}

export interface TrendChartProps {
  /** Card heading. */
  title: string;
  /** Optional muted subtitle under the title (e.g. the time range shown). */
  subtitle?: string;
  /** Series points (already mapped to the value being plotted). */
  data: TrendPoint[];
  /** Bucket granularity, used to format axis/tooltip labels. */
  granularity: Granularity;
  /** Whether the underlying query is loading. */
  loading?: boolean;
  /** Chart height in px (default 280). */
  height?: number;
  /** Formats the plotted value for the axis + tooltip (default: toLocaleString). */
  valueFormatter?: (value: number) => string;
  /** Optional control rendered in the card's top-right (e.g. granularity toggle). */
  extra?: React.ReactNode;
  /**
   * Force the empty state on/off. When omitted the chart falls back to its
   * default heuristic (every plotted value is zero). Callers that track
   * activity by a different signal — e.g. the user drawer keys "activity" off
   * messageCount, not costUsd — pass this so the empty state stays consistent
   * with the surrounding UI (a user with messages but zero spend still gets a
   * zero-line chart instead of a contradictory "No usage" placeholder).
   */
  isEmpty?: boolean;
  /**
   * Draw only the chart, with no card around it (SF-5124): for a chart inside a card that
   * already has its own header, such as the usage cards. The title still names the tooltip value.
   */
  bare?: boolean;
}

/**
 * Reusable area chart for a usage time series.
 * Empty/loading/data states are all handled internally so callers only pass
 * already-shaped points.
 */
export const TrendChart: React.FC<TrendChartProps> = ({
  title,
  subtitle,
  data,
  granularity,
  loading = false,
  height = 280,
  valueFormatter = (v) => v.toLocaleString(),
  extra,
  isEmpty,
  bare = false,
}) => {
  const { token } = theme.useToken();
  // Unique per-instance gradient id so multiple TrendCharts in one document
  // (e.g. the user drawer's daily + monthly charts, or a page's 30-day +
  // 12-month charts) don't share a single DOM id and bind their fills to the
  // wrong <linearGradient> definition.
  const rawId = useId();
  const gradientId = `evaTrendFill-${rawId.replace(/:/g, '')}`;

  const chartData = (data ?? []).map((point) => ({
    label: formatBucketLabel(point.bucket, granularity),
    value: Number.isFinite(point.value) ? point.value : 0,
  }));

  // Empty-state resolution. Callers that track activity by a different signal
  // (e.g. the user drawer keys "activity" off messageCount, not costUsd) pass
  // `isEmpty` to keep the empty state consistent with the surrounding UI. When
  // it's omitted, a series with any points is valid data even if every value
  // is $0 — a flat zero line is meaningful (a real month with no spend), so
  // only a series with no points at all is treated as empty.
  const hasData = isEmpty === undefined ? chartData.length > 0 : !isEmpty;

  // The right margin leaves room for the last x-axis label, which is centred on the last point.
  const content = loading ? (
    <Skeleton active paragraph={{ rows: 6 }} />
  ) : !hasData ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No usage in this period"
      style={{ paddingTop: height / 4 }}
    />
  ) : (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 8, right: 32, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={token.colorPrimary} stopOpacity={0.35} />
            <stop offset="95%" stopColor={token.colorPrimary} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 12, fill: token.colorTextSecondary }}
          tickLine={false}
          axisLine={{ stroke: token.colorBorderSecondary }}
          minTickGap={16}
        />
        <YAxis
          tick={{ fontSize: 12, fill: token.colorTextSecondary }}
          tickLine={false}
          axisLine={false}
          width={64}
          tickFormatter={valueFormatter}
        />
        <Tooltip
          formatter={(value: number) => [valueFormatter(value), title]}
          contentStyle={{
            borderRadius: token.borderRadius,
            borderColor: token.colorBorderSecondary,
            fontFamily: token.fontFamily,
          }}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={token.colorPrimary}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{ r: 4 }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  if (bare) return <div style={{ height }}>{content}</div>;

  return (
    <Card
      title={
        subtitle ? (
          <div>
            <div>{title}</div>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
              {subtitle}
            </Text>
          </div>
        ) : (
          title
        )
      }
      extra={extra}
      styles={{ body: { height } }}
    >
      {content}
    </Card>
  );
};
